import { useCallback, useEffect, useState } from 'react';
import { useInterval } from '@tenant/utility/hooks/useInterval';
import { getPipelineRunQuery } from '../apis';
import {
  PIPELINE_RUN_POLL_INTERVAL_MS,
  isTerminalPipelineRunStatus,
} from '../constants/pipelineRunStatus';

// Poll-based live run-status view (AC-13/14/15, OQ-7). Reuses the house `useInterval`
// hook (`delay = null` pauses it) rather than react-query's refetchInterval so the
// stop-on-terminal / teardown-on-unmount behaviour is explicit and directly inspectable
// (TC-DPW-025/026 are code-inspection cases).
//
// - Fetches immediately on mount / when pipelineId+runId change.
// - Polls at PIPELINE_RUN_POLL_INTERVAL_MS (3-5s, OQ-7 default) while non-terminal.
// - Stops polling once a terminal status (Succeeded/Failed) is observed, or once the
//   run/pipeline 404s (TC-DPW-041 — deleted mid-poll): `delay=null` on both.
// - A dropped/failed poll (network error, 5xx) does NOT stop polling and does NOT wipe
//   the last-known run — it surfaces a non-blocking `pollError` and retries next tick
//   (NFR-Reliability).
const usePollPipelineRun = ({ pipelineId, runId, enabled = true }) => {
  const [run, setRun] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pollError, setPollError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const isTerminal = !!run && isTerminalPipelineRunStatus(run.Status);

  const fetchOnce = useCallback(async () => {
    if (!pipelineId || !runId) return;
    try {
      const { data } = await getPipelineRunQuery({ pipelineId, runId });
      setRun(data);
      setPollError(null);
      setNotFound(false);
    } catch (error) {
      if (error?.response?.status === 404) {
        setNotFound(true);
      } else {
        setPollError('Connection issue — retrying…');
      }
    } finally {
      setIsLoading(false);
    }
  }, [pipelineId, runId]);

  // Reset + immediate fetch whenever the target run changes.
  useEffect(() => {
    setRun(null);
    setIsLoading(true);
    setPollError(null);
    setNotFound(false);
    if (enabled) {
      fetchOnce();
    }
  }, [pipelineId, runId, enabled, fetchOnce]);

  const shouldPoll = enabled && !!pipelineId && !!runId && !isTerminal && !notFound;

  useInterval(fetchOnce, shouldPoll ? PIPELINE_RUN_POLL_INTERVAL_MS : null);

  return { run, isLoading, pollError, notFound, isTerminal, refetch: fetchOnce };
};

export default usePollPipelineRun;
